import React, { useState, useEffect, useCallback } from 'react';
import * as Icons from 'lucide-react';

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8092' : `${window.location.protocol}//${window.location.hostname}:8092`;

const UserWAAutomation = () => {
  const [activeTab, setActiveTab] = useState('flows');
  const [flows, setFlows] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const [flowRes, qrRes, trigRes] = await Promise.all([
        fetch(`${API_BASE}/api/wa/automation/flows`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/wa/automation/quick-replies`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/wa/automation/triggers`, { headers }).catch(() => null),
      ]);
      if (flowRes?.ok) { const d = await flowRes.json(); setFlows(d.flows || []); }
      if (qrRes?.ok) { const d = await qrRes.json(); setQuickReplies(d.quick_replies || []); }
      if (trigRes?.ok) { const d = await trigRes.json(); setTriggers(d.triggers || []); }
    } catch (err) {
      console.error('Failed to fetch automation data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const statusColors = {
    active: { bg: '#dcfce7', text: '#166534' },
    paused: { bg: '#fef3c7', text: '#92400e' },
    draft: { bg: '#f3f4f6', text: '#374151' },
    archived: { bg: '#f3f4f6', text: '#6b7280' },
    inactive: { bg: '#f3f4f6', text: '#6b7280' },
  };

  const getIcon = (name, size = 18) => {
    const Icon = Icons[name];
    return Icon ? <Icon style={{ width: size, height: size }} /> : null;
  };

  const renderEmpty = (icon, title, desc, btnLabel, btnAction) => (
    <div className="text-center py-12">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(37, 211, 102, 0.08)' }}>
        {getIcon(icon, 32)}
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-500 mb-4">{desc}</p>
      {btnLabel && (
        <button onClick={btnAction} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
          <Icons.Plus className="w-4 h-4" /> {btnLabel}
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
              <Icons.Workflow className="w-5 h-5 text-white" />
            </div>
            Automation
          </h1>
          <p className="text-sm text-gray-500 mt-1">Build chatbots, auto-replies, and workflow automations</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Active Flows', value: flows.filter(f => f.status === 'active').length || '0', icon: 'Workflow', color: '#25D366' },
          { label: 'Quick Replies', value: quickReplies.filter(q => q.status === 'active').length || '0', icon: 'Zap', color: '#128C7E' },
          { label: 'Event Triggers', value: triggers.filter(t => t.status === 'active').length || '0', icon: 'Bell', color: '#34B7F1' },
          { label: 'Messages Automated', value: flows.reduce((s, f) => s + (f.total_sent || 0), 0).toLocaleString(), icon: 'Bot', color: '#075E54' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}14`, color: stat.color }}>
                {getIcon(stat.icon, 20)}
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {[
            { key: 'flows', label: 'Chat Flows', icon: 'Workflow' },
            { key: 'quick-replies', label: 'Quick Replies', icon: 'Zap' },
            { key: 'triggers', label: 'Event Triggers', icon: 'Bell' },
            { key: 'chatbot', label: 'Chatbot Builder', icon: 'Bot' },
          ].map((tab) => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-all border-b-2 whitespace-nowrap"
              style={{
                borderColor: activeTab === tab.key ? '#25D366' : 'transparent',
                color: activeTab === tab.key ? '#25D366' : '#6b7280',
                background: activeTab === tab.key ? 'rgba(37, 211, 102, 0.04)' : 'transparent',
              }}
            >
              {getIcon(tab.icon, 16)}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <Icons.Loader2 className="w-8 h-8 animate-spin mx-auto text-green-500 mb-3" />
              <p className="text-sm text-gray-500">Loading automation data...</p>
            </div>
          ) : (
            <>
              {activeTab === 'flows' && (
                flows.length === 0 ?
                  renderEmpty('Workflow', 'No Chat Flows', 'Create automated chat flows to handle common customer interactions.', 'Create Flow') :
                  <div className="space-y-4">
                    <div className="flex justify-end">
                      <button className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                        <Icons.Plus className="w-4 h-4" /> Create Flow
                      </button>
                    </div>
                    {flows.map((flow) => (
                      <div key={flow.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:border-green-200 hover:shadow-sm transition-all">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(37, 211, 102, 0.08)' }}>
                            <Icons.Workflow className="w-5 h-5" style={{ color: '#25D366' }} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 text-sm">{flow.name}</h3>
                            <p className="text-xs text-gray-500 mt-0.5">Trigger: {flow.trigger_type}{flow.trigger_value ? ` (${flow.trigger_value})` : ''} | {flow.total_steps || 0} steps</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="hidden md:flex items-center gap-4 text-xs text-gray-500">
                            <span>Sent: <strong className="text-gray-700">{(flow.total_sent || 0).toLocaleString()}</strong></span>
                            <span>Completed: <strong className="text-green-600">{(flow.total_completed || 0).toLocaleString()}</strong></span>
                          </div>
                          <span className="px-2.5 py-1 rounded-lg text-xs font-semibold" style={{
                            background: statusColors[flow.status]?.bg, color: statusColors[flow.status]?.text,
                          }}>{(flow.status || 'draft').toUpperCase()}</span>
                          <div className="flex items-center gap-1">
                            <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"><Icons.Pencil className="w-4 h-4" /></button>
                            <button className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"><Icons.Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
              )}

              {activeTab === 'quick-replies' && (
                quickReplies.length === 0 ?
                  renderEmpty('Zap', 'No Quick Replies', 'Set up keyword-based auto-responses for common questions.', 'Add Quick Reply') :
                  <div className="space-y-4">
                    <div className="flex justify-end">
                      <button className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                        <Icons.Plus className="w-4 h-4" /> Add Quick Reply
                      </button>
                    </div>
                    {quickReplies.map((qr) => (
                      <div key={qr.id} className="p-4 rounded-xl border border-gray-100 hover:border-green-200 transition-all">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className="px-2.5 py-1 rounded-lg text-xs font-bold" style={{ background: 'rgba(37, 211, 102, 0.1)', color: '#128C7E' }}>{qr.keyword}</span>
                            <span className="text-xs text-gray-400">Match: {qr.match_type}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded-md text-xs font-medium" style={{ background: statusColors[qr.status]?.bg, color: statusColors[qr.status]?.text }}>{qr.status}</span>
                            <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><Icons.Pencil className="w-3.5 h-3.5" /></button>
                            <button className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"><Icons.Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{qr.response_text}</p>
                      </div>
                    ))}
                  </div>
              )}

              {activeTab === 'triggers' && (
                triggers.length === 0 ?
                  renderEmpty('Bell', 'No Event Triggers', 'Set up automated actions triggered by specific events.', 'Add Trigger') :
                  <div className="space-y-4">
                    <div className="flex justify-end">
                      <button className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                        <Icons.Plus className="w-4 h-4" /> Add Trigger
                      </button>
                    </div>
                    {triggers.map((trigger) => (
                      <div key={trigger.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:border-green-200 transition-all">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(37, 211, 102, 0.08)' }}>
                            <Icons.Bell className="w-5 h-5" style={{ color: '#25D366' }} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 text-sm">{trigger.name}</h3>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                              <span className="flex items-center gap-1"><Icons.Zap className="w-3 h-3" /> {trigger.event_type}</span>
                              <span className="flex items-center gap-1"><Icons.Clock className="w-3 h-3" /> Delay: {trigger.delay_seconds || 0}s</span>
                              <span className="capitalize">{(trigger.action_type || '').replace('_', ' ')}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="px-2.5 py-1 rounded-lg text-xs font-semibold" style={{ background: statusColors[trigger.status]?.bg, color: statusColors[trigger.status]?.text }}>
                            {(trigger.status || 'draft').toUpperCase()}
                          </span>
                          <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><Icons.Pencil className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
              )}

              {activeTab === 'chatbot' && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, rgba(37, 211, 102, 0.1), rgba(18, 140, 126, 0.1))' }}>
                    <Icons.Bot className="w-10 h-10" style={{ color: '#25D366' }} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Visual Chatbot Builder</h3>
                  <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
                    Design conversational flows with our drag-and-drop chatbot builder. Create interactive menus, collect user data, and automate responses.
                  </p>
                  <div className="flex justify-center gap-3">
                    <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-medium text-sm" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                      <Icons.Plus className="w-4 h-4" /> Create New Bot
                    </button>
                    <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-gray-600 font-medium text-sm border border-gray-200 hover:bg-gray-50">
                      <Icons.FileText className="w-4 h-4" /> Use Template
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserWAAutomation;
